import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SheetCompanyInvite from './_partials/sheets/SheetCompanyInvite';
import SheetCollaboratorEdit from './_partials/sheets/SheetCollaboratorEdit';
import SheetCompanyEdit from './_partials/sheets/SheetCompanyEdit';
import SheetActivityFilter from './_partials/sheets/SheetActivityFilter';
import SheetUserBillingEdit from './_partials/sheets/SheetUserBillingEdit';
import SheetUserEmailEdit from './_partials/sheets/SheetUserEmailEdit';
import SheetUserPasswordEdit from './_partials/sheets/SheetUserPasswordEdit';
import SheetSubsidiaryCreate from './_partials/sheets/SheetSubsidiaryCreate';
import AlertDialogDeleteCollaborator from './_partials/dialogs/AlertDialogDeleteCollaborator';
import AlertDialogReactivateCollaborator from './_partials/dialogs/AlertDialogReactivateCollaborator';
import AccountTabContent from './_partials/AccountTabContent';
import CompanyTabContent from './_partials/CompanyTabContent';
import SuperAdminTabContent from './_partials/SuperAdminTabContent';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/contexts/auth-context';
import { useBalance } from '@/lib/contexts/balance-context';
import { useTheme } from '@/lib/contexts/theme-context';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { useElementHeight } from '@/lib/hooks/use-element-height';

export default function Account() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation('account');
    const { user: authUser, refreshUser, isLoading: isAuthLoading } = useAuth();
    const { getCache, setCache, invalidateCache } = useApiCache();
    const { balance: companyBalance } = useBalance();
    const { theme } = useTheme();
    const isHorodateme = theme.slug === 'horodateme';

    const LOCATION_QUALITY_STORAGE_KEY = 'horodaty_location_quality';
    const NOTIFICATIONS_STORAGE_KEY = 'horodaty_notifications_preferences_v1';

    const companyInfoRef = useRef(null);

    const [inviteOpen, setInviteOpen] = useState(false);
    const [editCollaboratorOpen, setEditCollaboratorOpen] = useState(false);
    const [selectedCollaborator, setSelectedCollaborator] = useState(null);
    const [deleteCollaboratorOpen, setDeleteCollaboratorOpen] = useState(false);
    const [collaboratorToDelete, setCollaboratorToDelete] = useState(null);
    const [reactivateCollaboratorOpen, setReactivateCollaboratorOpen] = useState(false);
    const [collaboratorToReactivate, setCollaboratorToReactivate] = useState(null);
    const [pendingInvites, setPendingInvites] = useState([]);

    const [companyEditOpen, setCompanyEditOpen] = useState(false);
    const [activityFilterOpen, setActivityFilterOpen] = useState(false);
    const [activityFilter, setActivityFilter] = useState('all');
    const [subsidiaryCreateOpen, setSubsidiaryCreateOpen] = useState(false);

    const [billingEditOpen, setBillingEditOpen] = useState(false);
    const [emailEditOpen, setEmailEditOpen] = useState(false);
    const [passwordEditOpen, setPasswordEditOpen] = useState(false);
    const [sheetResetKey, setSheetResetKey] = useState(0);

    const [isLoadingAccount, setIsLoadingAccount] = useState(true);
    const [accountError, setAccountError] = useState('');
    const [settingsError, setSettingsError] = useState('');
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [isSubmittingSheet, setIsSubmittingSheet] = useState(false);
    const [isSendingVerification, setIsSendingVerification] = useState(false);

    const [user, setUser] = useState(null);
    const [settings, setSettings] = useState(null);

    const [company, setCompany] = useState(null);
    const [companyLogoUrl, setCompanyLogoUrl] = useState('');
    const [collaborators, setCollaborators] = useState([]);
    const [collaboratorSearch, setCollaboratorSearch] = useState('');
    const [collaboratorsPage, setCollaboratorsPage] = useState(1);
    const [collaboratorsPageSize, setCollaboratorsPageSize] = useState(10);
    const [collaboratorsPageCount, setCollaboratorsPageCount] = useState(1);
    const [collaboratorsTotal, setCollaboratorsTotal] = useState(0);
    const [collaboratorsSearchApi, setCollaboratorsSearchApi] = useState('');
    const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
    const [activities, setActivities] = useState([]);
    const [isLoadingActivities, setIsLoadingActivities] = useState(false);
    const [activitiesPage, setActivitiesPage] = useState(1);
    const [activitiesPageSize, setActivitiesPageSize] = useState(10);
    const [activitiesPageCount, setActivitiesPageCount] = useState(1);
    const [activitiesTotal, setActivitiesTotal] = useState(0);
    const [subsidiaries, setSubsidiaries] = useState([]);
    const [isLoadingSubsidiaries, setIsLoadingSubsidiaries] = useState(false);
    const [subsidiarySearch, setSubsidiarySearch] = useState('');
    const [subsidiariesPage, setSubsidiariesPage] = useState(1);
    const [subsidiariesPageSize, setSubsidiariesPageSize] = useState(10);
    const [subsidiariesPageCount, setSubsidiariesPageCount] = useState(1);
    const [subsidiariesTotal, setSubsidiariesTotal] = useState(0);
    const [subsidiariesSearchApi, setSubsidiariesSearchApi] = useState('');
    // Use authUser for hasCompany to avoid race condition on page reload
    const companyId =
        authUser?.company_uniq_id ??
        authUser?.company?.uniq_id ??
        user?.company_uniq_id ??
        user?.company?.uniq_id ??
        null;
    const hasCompany = Boolean(companyId);
    const isSuperAdmin = authUser?.role === 'admin';
    const isEmailVerified = Boolean(user?.email_verified);

    // Determine initial tab from URL on mount
    const [activeTab, setActiveTab] = useState(() => {
        const path = window.location.pathname;
        if (path.startsWith('/settings/superadmin')) return 'superadmin';
        return path.endsWith('/company') || path === '/settings/company' ? 'company' : 'account';
    });

    // Sync activeTab with URL changes (only after initial load, and only if user navigates)
    const initialLoadDone = useRef(false);
    useEffect(() => {
        // Skip first render to preserve URL-based initial state
        if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            return;
        }
        const wantsSuperAdmin = location.pathname.startsWith('/settings/superadmin');
        const wantsCompany = location.pathname.endsWith('/company') || location.pathname === '/settings/company';
        if (wantsSuperAdmin && isSuperAdmin) setActiveTab('superadmin');
        else setActiveTab(wantsCompany && hasCompany ? 'company' : 'account');
    }, [location.pathname, hasCompany, isSuperAdmin]);

    const collaboratorsHeight = useElementHeight(companyInfoRef, [activeTab, company]);

    const firstName = user?.first_name ?? '';
    const lastName = user?.last_name ?? '';
    const email = user?.email ?? '';
    const companyName = user?.company?.name ?? user?.company?.company_name ?? user?.company?.label ?? '';

    const processInvites = async (invites) => {
        setIsSubmittingSheet(true);
        setAccountError('');

        // Clone to avoid mutating state directly
        const remaining = [...invites];
        let successCount = 0;

        while (remaining.length > 0) {
            const invite = remaining[0];

            try {                
                await horodatyApi.managerCreateUserV2({
                    first_name: invite.firstName,
                    last_name: invite.lastName,
                    email: invite.email,
                    role: invite.role,
                });

                // Success: remove from list
                remaining.shift();
                successCount++;
            } catch (error) {
                // Check for archived user error ERR_CRE_0005
                if (error?.response?.data?.code === 'ERR_CRE_0005') {
                    const archivedUser = error?.response?.data?.data?.existing_user;
                    if (archivedUser) {
                        // Pause processing
                        remaining.shift(); // Remove current invite from queue (processed via exception flow)

                        if (successCount > 0) {
                            toast.success(t('page.invitationsSent', { count: successCount }));
                        }

                        setPendingInvites(remaining);
                        setCollaboratorToReactivate({
                            ...archivedUser,
                            name: `${invite.firstName} ${invite.lastName}`.trim() || archivedUser.name,
                        });
                        setReactivateCollaboratorOpen(true);

                        // Close invite sheet
                        setInviteOpen(false);
                        setIsSubmittingSheet(false);
                        return; // Exit function, wait for user input
                    }
                }

                const message = error?.response?.data?.message || t('page.collaboratorAddFailed');
                const errorMsg = `${message} (${invite.email})`;

                setAccountError(errorMsg);
                toast.error(errorMsg);

                if (successCount > 0) {
                    toast.success(t('page.invitationsSent', { count: successCount }));
                }

                setIsSubmittingSheet(false);
                return;
            }
        }

        // All done
        await refreshCollaborators(true);
        setInviteOpen(false);
        toast.success(t('page.invitationsSent', { count: successCount }));
        setIsSubmittingSheet(false);
    };

    const handleSendVerificationEmail = async () => {
        setIsSendingVerification(true);
        try {
            await horodatyApi.sendEmailVerification();
            toast.success(t('page.verificationEmailSent'));
        } catch (error) {
            const message = error?.response?.data?.message || t('page.verificationEmailFailed');
            toast.error(message);
        } finally {
            setIsSendingVerification(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleDateString('fr-FR');
        } catch {
            return '—';
        }
    };

    const normalize01Flag = (value) => {
        if (value === true) return true;
        if (value === false) return false;
        const num = Number(value);
        if (Number.isFinite(num)) return num === 1;
        return Boolean(value);
    };

    const refreshCompany = useCallback(
        async (forceRefresh = false) => {
            if (!companyId) return;

            const cacheKey = `company:${companyId}`;
            if (!forceRefresh) {
                const cached = getCache(cacheKey, 'company');
                if (cached) {
                    setCompany(cached);
                    return;
                }
            }

            try {
                const companyRes = await horodatyApi.getCompany(companyId);
                const data = companyRes?.data?.data ?? null;
                setCompany(data);
                // Only cache if we got valid data
                if (data) {
                    setCache(cacheKey, data, 'company');
                } else {
                    // Invalidate cache if response was empty
                    invalidateCache(cacheKey);
                }
            } catch {
                // Don't cache errors - invalidate any existing cache
                invalidateCache(cacheKey);
            }
        },
        [companyId],
    );

    const refreshCompanyLogo = useCallback(
        async (forceRefresh = false) => {
            if (!companyId) return;

            const cacheKey = `companyLogo:${companyId}`;
            if (!forceRefresh) {
                const cached = getCache(cacheKey, 'static');
                if (cached) {
                    setCompanyLogoUrl(cached);
                    return;
                }
            }

            try {
                const res = await horodatyApi.getCompanyLogo(companyId);
                const blob = res?.data;
                if (!blob) {
                    invalidateCache(cacheKey);
                    return;
                }

                const url = URL.createObjectURL(blob);
                setCompanyLogoUrl((prev) => {
                    if (prev) {
                        try {
                            URL.revokeObjectURL(prev);
                        } catch {
                            // ignore
                        }
                    }
                    return url;
                });
                setCache(cacheKey, url, 'static');
            } catch {
                // Don't cache errors - invalidate any existing cache
                invalidateCache(cacheKey);
            }
        },
        [companyId],
    );

    const refreshCollaborators = useCallback(
        async (forceRefresh = false) => {
            if (!companyId) return;

            setIsLoadingCollaborators(true);
            try {
                const res = await horodatyApi.getCompanyUsers(companyId, {
                    paginate: true,
                    page: collaboratorsPage,
                    per_page: collaboratorsPageSize,
                    ...(collaboratorsSearchApi ? { search: collaboratorsSearchApi } : {}),
                });
                const list = res?.data?.data ?? [];
                const meta = res?.data?.meta;
                const mapped = (Array.isArray(list) ? list : [])
                    .filter((u) => u?.status !== 'Inactif' && u?.status !== 'INACTIVE')
                    .map((u) => ({
                        id: String(u?.uniq_id ?? u?.id ?? ''),
                        firstName: u?.first_name ?? '',
                        lastName: u?.last_name ?? '',
                        name: `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() || u?.email || '—',
                        email: u?.email ?? '—',
                        role: u?.role ?? null,
                        status: u?.status ?? null,
                        raw: u,
                    }));
                setCollaborators(mapped);
                if (meta) {
                    setCollaboratorsPageCount(meta.last_page ?? 1);
                    setCollaboratorsTotal(meta.total ?? 0);
                }
            } catch {
                // silent
            } finally {
                setIsLoadingCollaborators(false);
            }
        },
        [companyId, collaboratorsPage, collaboratorsPageSize, collaboratorsSearchApi],
    );

    const collaboratorSearchTimeout = useRef(null);
    const handleCollaboratorSearchChange = useCallback((value) => {
        setCollaboratorSearch(value);
        if (collaboratorSearchTimeout.current) clearTimeout(collaboratorSearchTimeout.current);
        collaboratorSearchTimeout.current = setTimeout(() => {
            setCollaboratorsSearchApi(value);
            setCollaboratorsPage(1);
        }, 400);
    }, []);

    const refreshCompanyTransactions = useCallback(
        async (forceRefresh = false) => {
            if (!companyId) return;

            const cacheKey = `companyTransactions:${companyId}:p${activitiesPage}:s${activitiesPageSize}:f${Array.isArray(activityFilter) ? activityFilter.join(',') : activityFilter}`;
            if (!forceRefresh) {
                const cached = getCache(cacheKey, 'default');
                if (cached) {
                    setActivities(cached.data);
                    setActivitiesPageCount(cached.pageCount);
                    setActivitiesTotal(cached.total);
                    return;
                }
            }

            setIsLoadingActivities(true);
            try {
                const res = await horodatyApi.getCompanyTransactions(companyId, {
                    paginate: true,
                    page: activitiesPage,
                    per_page: activitiesPageSize,
                    ...(Array.isArray(activityFilter) ? { user_ids: activityFilter.join(',') } : {}),
                });
                const list = res?.data?.data ?? [];
                const meta = res?.data?.meta;

                // Mapper le type de transaction vers un type lisible pour l'UI
                const usageToType = (usage) => {
                    if (!usage) return 'tokens';
                    const usageLower = usage.toLowerCase();
                    if (usageLower.includes('photo') || usageLower.includes('picture')) return 'photo';
                    if (
                        usageLower.includes('procedure') ||
                        usageLower.includes('intervention') ||
                        usageLower.includes('rapport')
                    )
                        return 'procedure_filled';
                    if (usageLower.includes('dossier') || usageLower.includes('folder')) return 'procedure_created';
                    if (
                        usageLower.includes('achat') ||
                        usageLower.includes('credit') ||
                        usageLower.includes('purchase')
                    )
                        return 'tokens';
                    if (usageLower.includes('invite') || usageLower.includes('user')) return 'invitation';
                    return 'tokens';
                };

                const mapped = (Array.isArray(list) ? list : []).map((transaction) => {
                    const delta = transaction.type === 'credit' ? transaction.token_amount : -transaction.token_amount;

                    return {
                        id: String(transaction?.uniq_id ?? Math.random()),
                        title: transaction?.usage || transaction?.message || t('account:transaction'),
                        type: usageToType(transaction?.usage),
                        date: formatDate(transaction?.created_at),
                        delta: delta,
                        collaborator: transaction?.user || null,
                    };
                });

                setActivities(mapped);
                if (meta) {
                    setActivitiesPageCount(meta.last_page ?? 1);
                    setActivitiesTotal(meta.total ?? 0);
                }
                // Only cache if we got valid data
                if (res?.data?.data !== undefined) {
                    setCache(
                        cacheKey,
                        { data: mapped, pageCount: meta?.last_page ?? 1, total: meta?.total ?? 0 },
                        'default',
                    );
                }
            } catch {
                // Don't cache errors - invalidate any existing cache
                invalidateCache(cacheKey);
            } finally {
                setIsLoadingActivities(false);
            }
        },
        [companyId, activitiesPage, activitiesPageSize, activityFilter, t],
    );

    const refreshSubsidiaries = useCallback(
        async (forceRefresh = false) => {
            if (!companyId) return;

            setIsLoadingSubsidiaries(true);
            try {
                const res = await horodatyApi.subsidiaries.list(companyId, {
                    paginate: true,
                    page: subsidiariesPage,
                    per_page: subsidiariesPageSize,
                    ...(subsidiariesSearchApi ? { search: subsidiariesSearchApi } : {}),
                });
                const list = res?.data?.data ?? [];
                const meta = res?.data?.meta;
                const mapped = (Array.isArray(list) ? list : []).map((sub) => ({
                    ...sub,
                    id: sub.uniq_id ?? sub.id,
                    phone: sub.phone ?? sub.phone_number ?? null,
                    address: [sub.street, sub.cp, sub.city].filter(Boolean).join(', ') || null,
                }));
                setSubsidiaries(mapped);
                if (meta) {
                    setSubsidiariesPageCount(meta.last_page ?? 1);
                    setSubsidiariesTotal(meta.total ?? 0);
                }
            } catch {
                // silent
            } finally {
                setIsLoadingSubsidiaries(false);
            }
        },
        [companyId, subsidiariesPage, subsidiariesPageSize, subsidiariesSearchApi],
    );

    const subsidiarySearchTimeout = useRef(null);
    const handleSubsidiarySearchChange = useCallback((value) => {
        setSubsidiarySearch(value);
        if (subsidiarySearchTimeout.current) clearTimeout(subsidiarySearchTimeout.current);
        subsidiarySearchTimeout.current = setTimeout(() => {
            setSubsidiariesSearchApi(value);
            setSubsidiariesPage(1);
        }, 400);
    }, []);

    // Notification settings (API exposes `mails: 0|1`)
    const [emailPromotions, setEmailPromotions] = useState(false);
    const [emailUpdates, setEmailUpdates] = useState(false);
    const [mobilePromotions, setMobilePromotions] = useState(false);
    const [mobileUpdates, setMobileUpdates] = useState(false);

    // Location quality (no documented API field; stored locally)
    // Horodateme : toujours la précision la plus élevée, sans choix
    const [locationQuality, setLocationQuality] = useState(isHorodateme ? 'elevee' : 'moyenne');
    const locationOptions = [
        { value: 'faible', label: t('page.quality.low'), subtitle: '~100m' },
        { value: 'moyenne', label: t('page.quality.medium'), subtitle: '~10m' },
        { value: 'elevee', label: t('page.quality.high'), subtitle: '~5m' },
    ];

    // Photo quality (API field: settings.quality = MEDIUM|HIGH|MAX)
    const [photoQuality, setPhotoQuality] = useState('moyenne');
    const photoOptions = [
        { value: 'basse', label: t('page.photoQualityLow') },
        { value: 'moyenne', label: t('page.quality.medium') },
        { value: 'elevee', label: t('page.quality.high') },
    ];

    // Auto save (API field: settings.download_auto = 0|1)
    const [autoSave, setAutoSave] = useState(true);

    const uiPhotoQualityToApi = (value) => {
        switch (value) {
            case 'basse':
                return 'MEDIUM';
            case 'elevee':
                return 'MAX';
            case 'moyenne':
            default:
                return 'HIGH';
        }
    };

    const apiPhotoQualityToUi = (value) => {
        switch (value) {
            case 'MEDIUM':
                return 'basse';
            case 'MAX':
                return 'elevee';
            case 'HIGH':
            default:
                return 'moyenne';
        }
    };

    const loadNotificationPreferences = () => {
        try {
            const raw = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') return null;
            return {
                emailPromotions: Boolean(parsed.emailPromotions),
                emailUpdates: Boolean(parsed.emailUpdates),
                mobilePromotions: Boolean(parsed.mobilePromotions),
                mobileUpdates: Boolean(parsed.mobileUpdates),
            };
        } catch {
            return null;
        }
    };

    const persistNotificationPreferences = (next) => {
        try {
            localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(next));
        } catch {
            // ignore
        }
    };

    const computeMailsFlag = (prefs) => {
        const anyEnabled = Boolean(
            prefs?.emailPromotions || prefs?.emailUpdates || prefs?.mobilePromotions || prefs?.mobileUpdates,
        );
        return anyEnabled ? 1 : 0;
    };

    const saveSettings = async (partial) => {
        setIsSavingSettings(true);
        setSettingsError('');
        try {
            const res = await horodatyApi.updateSettings(partial);
            const next = res?.data?.settings ?? null;
            const userId = user?.uniq_id ?? user?.id;
            if (next && userId) {
                setSettings(next);
                setCache(`settings:${userId}`, next, 'settings');
                if (Object.prototype.hasOwnProperty.call(partial, 'download_auto')) {
                    setAutoSave(normalize01Flag(next?.download_auto));
                }
            }
            toast.success(t('page.settingsSaved'));
        } catch (error) {
            const message = error?.response?.data?.message || t('page.settingsSaveFailed');
            setSettingsError(message);
            toast.error(message);
            throw error;
        } finally {
            setIsSavingSettings(false);
        }
    };

    useEffect(() => {
        if (isHorodateme) {
            // Horodateme : forcer la précision maximale
            setLocationQuality('elevee');
            localStorage.setItem(LOCATION_QUALITY_STORAGE_KEY, 'elevee');
        } else {
            const stored = localStorage.getItem(LOCATION_QUALITY_STORAGE_KEY);
            if (stored) setLocationQuality(stored);
        }
    }, [isHorodateme]);

    useEffect(() => {
        // Persist notification preferences locally (API only has a single `mails` flag)
        persistNotificationPreferences({
            emailPromotions,
            emailUpdates,
            mobilePromotions,
            mobileUpdates,
        });
    }, [emailPromotions, emailUpdates, mobilePromotions, mobileUpdates]);

    useEffect(() => {
        // Wait for auth to finish loading before fetching account data
        // Also ensure authUser is populated to avoid premature "User ID not available" error
        if (isAuthLoading || !authUser) return;

        let isMounted = true;

        async function loadAccount() {
            setIsLoadingAccount(true);
            setAccountError('');
            setSettingsError('');

            try {
                // Get userId from auth context (should be available after auth loading)
                const userPayload = authUser;
                const userId = authUser?.uniq_id ?? authUser?.id;

                if (!userId) throw new Error("L'identifiant utilisateur n'est pas disponible.");

                const settingsCacheKey = `settings:${userId}`;

                // Check cache for settings with proper user namespace
                const cachedSettings = getCache(settingsCacheKey, 'settings');
                let settingsPayload = cachedSettings;

                // Fetch settings if not in cache
                if (!cachedSettings) {
                    const settingsRes = await horodatyApi.getSettings();
                    settingsPayload = settingsRes?.data?.settings ?? null;
                    if (settingsPayload) {
                        setCache(settingsCacheKey, settingsPayload, 'settings');
                    }
                }

                if (!isMounted) return;

                setUser(userPayload);
                setSettings(settingsPayload);

                if (settingsPayload && typeof settingsPayload === 'object') {
                    if (typeof settingsPayload.download_auto !== 'undefined') {
                        setAutoSave(normalize01Flag(settingsPayload.download_auto));
                    }
                    if (typeof settingsPayload.mails !== 'undefined') {
                        const mailsEnabled = normalize01Flag(settingsPayload.mails);
                        if (!mailsEnabled) {
                            setEmailPromotions(false);
                            setEmailUpdates(false);
                            setMobilePromotions(false);
                            setMobileUpdates(false);
                        } else {
                            const storedPrefs = loadNotificationPreferences();
                            if (storedPrefs) {
                                setEmailPromotions(storedPrefs.emailPromotions);
                                setEmailUpdates(storedPrefs.emailUpdates);
                                setMobilePromotions(storedPrefs.mobilePromotions);
                                setMobileUpdates(storedPrefs.mobileUpdates);
                            } else {
                                // Default: enable all if the server flag is enabled
                                setEmailPromotions(true);
                                setEmailUpdates(true);
                                setMobilePromotions(true);
                                setMobileUpdates(true);
                            }
                        }
                    }
                    if (typeof settingsPayload.quality !== 'undefined') {
                        setPhotoQuality(apiPhotoQualityToUi(settingsPayload.quality));
                    }
                }
            } catch (error) {
                console.error('Error loading account data:', error);
                if (!isMounted) return;
                const message = error?.response?.data?.message || t('account:impossibleDeChargerLesInformationsDuCompte');
                setAccountError(message);
            } finally {
                if (!isMounted) return;
                setIsLoadingAccount(false);
            }
        }

        loadAccount();

        return () => {
            isMounted = false;
        };
    }, [isAuthLoading, authUser]);

    useEffect(() => {
        // When navigating to /settings/company, load company-related data.
        if (activeTab !== 'company') return;
        if (!companyId) return;

        let isMounted = true;

        (async () => {
            try {
                const hash = window.location.hash?.replace('#', '');

                if (hash === 'actions-history') {
                    // Priority-load activities first when scroll target is the history section
                    await refreshCompanyTransactions();
                    await Promise.all([
                        refreshCompany(),
                        refreshCompanyLogo(),
                        refreshCollaborators(),
                        refreshSubsidiaries(),
                    ]);
                } else {
                    await Promise.all([
                        refreshCompany(),
                        refreshCompanyLogo(),
                        refreshCollaborators(),
                        refreshCompanyTransactions(),
                        refreshSubsidiaries(),
                    ]);
                }
            } catch {
                // keep UI minimal
            }
        })();

        return () => {
            isMounted = false;
            void isMounted;
        };
    }, [activeTab, companyId]);

    // Re-fetch activities when pagination or filter changes
    useEffect(() => {
        if (activeTab !== 'company' || !companyId) return;
        refreshCompanyTransactions(true);
    }, [activitiesPage, activitiesPageSize, activityFilter]);

    // Refetch collaborators when pagination/search changes
    useEffect(() => {
        if (activeTab !== 'company' || !companyId) return;
        refreshCollaborators();
    }, [collaboratorsPage, collaboratorsPageSize, collaboratorsSearchApi]);

    // Refetch subsidiaries when pagination/search changes
    useEffect(() => {
        if (activeTab !== 'company' || !companyId) return;
        refreshSubsidiaries();
    }, [subsidiariesPage, subsidiariesPageSize, subsidiariesSearchApi]);

    // Scroll to a section when hash is present (e.g. #actions-history)
    const scrollToHandled = useRef(false);
    useEffect(() => {
        if (isLoadingActivities) return;
        if (scrollToHandled.current) return;
        const hash = location.hash?.replace('#', '');
        if (!hash) return;

        // Small delay for DOM to settle after data render
        const timer = setTimeout(() => {
            const el = document.getElementById(hash);
            if (el) {
                // Find the nearest scrollable ancestor to avoid scrolling <html>
                let scrollParent = el.parentElement;
                while (scrollParent) {
                    const { overflow, overflowY } = getComputedStyle(scrollParent);
                    if (/auto|scroll/.test(overflow + overflowY)) break;
                    scrollParent = scrollParent.parentElement;
                }
                if (scrollParent) {
                    const offset =
                        el.getBoundingClientRect().top -
                        scrollParent.getBoundingClientRect().top +
                        scrollParent.scrollTop;
                    scrollParent.scrollTo({ top: offset, behavior: 'smooth' });
                }
                scrollToHandled.current = true;
                // Clean up the hash
                navigate(location.pathname + location.search, { replace: true });
            }
        }, 150);

        return () => clearTimeout(timer);
    }, [isLoadingActivities, location.hash, navigate, location.pathname, location.search]);

    useEffect(() => {
        // If the user has no company, prevent /settings/company.
        // Wait for authUser to be fully loaded before redirecting
        const isCompanyPath = location.pathname.endsWith('/company') || location.pathname === '/settings/company';
        if (isAuthLoading || !authUser) return;
        if (isCompanyPath && (!hasCompany || authUser?.role === 'user')) {
            navigate('/settings/account', { replace: true });
        }
    }, [location.pathname, hasCompany, isAuthLoading, authUser, navigate]);

    const companyLogoUrlRef = useRef(companyLogoUrl);
    useEffect(() => {
        companyLogoUrlRef.current = companyLogoUrl;
    }, [companyLogoUrl]);

    useEffect(() => {
        return () => {
            const url = companyLogoUrlRef.current;
            if (!url) return;
            try {
                URL.revokeObjectURL(url);
                invalidateCache(`companyLogo:${companyId}`);
            } catch {
                // ignore
            }
        };
    }, [companyId]);

    const tabs = [
        { value: 'account', label: t('account:monCompte') },
        ...(hasCompany && authUser?.role !== 'user' ? [{ value: 'company', label: t('account:monEntreprise') }] : []),
        ...(isSuperAdmin ? [{ value: 'superadmin', label: t('account:superadmin') }] : []),
    ];

    const handleTabChange = (value) => {
        if (value === 'superadmin') {
            navigate('/settings/superadmin');
        } else if (value === 'company') {
            if (hasCompany) navigate('/settings/company');
            else navigate('/settings/account');
        } else {
            navigate('/settings/account');
        }
    };

    return (
        <div className="pb-[120px] md:pb-8 space-y-6">
            {/* Tab Navigation */}
            {hasCompany || isSuperAdmin ? (
                <div className="bg-card border-b border-border px-5 pt-4 pb-0">
                    <Tabs value={activeTab} onValueChange={handleTabChange}>
                        <TabsList className="bg-transparent p-0 h-auto gap-8">
                            {tabs.map((tab) => (
                                <TabsTrigger
                                    key={tab.value}
                                    value={tab.value}
                                    className="px-0 py-0 pb-3 rounded-none font-poppins text-xl font-semibold 
                                        text-muted-foreground bg-transparent shadow-none
                                        data-[state=active]:text-foreground data-[state=active]:bg-transparent 
                                        data-[state=active]:shadow-none data-[state=active]:border-b-[3px] 
                                        data-[state=active]:border-secondary hover:text-foreground
                                        transition-colors"
                                >
                                    {tab.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>
            ) : null}

            {/* Content */}
            {activeTab === 'account' && (
                <AccountTabContent
                    user={user}
                    firstName={firstName}
                    lastName={lastName}
                    email={email}
                    companyName={companyName}
                    isEmailVerified={isEmailVerified}
                    isLoadingAccount={isLoadingAccount}
                    accountError={accountError}
                    settingsError={settingsError}
                    isSendingVerification={isSendingVerification}
                    handleSendVerificationEmail={handleSendVerificationEmail}
                    setBillingEditOpen={setBillingEditOpen}
                    setEmailEditOpen={setEmailEditOpen}
                    setPasswordEditOpen={setPasswordEditOpen}
                    emailPromotions={emailPromotions}
                    emailUpdates={emailUpdates}
                    mobilePromotions={mobilePromotions}
                    mobileUpdates={mobileUpdates}
                    setEmailPromotions={setEmailPromotions}
                    setEmailUpdates={setEmailUpdates}
                    setMobilePromotions={setMobilePromotions}
                    setMobileUpdates={setMobileUpdates}
                    computeMailsFlag={computeMailsFlag}
                    saveSettings={saveSettings}
                    isSavingSettings={isSavingSettings}
                    locationQuality={locationQuality}
                    setLocationQuality={setLocationQuality}
                    locationOptions={locationOptions}
                    LOCATION_QUALITY_STORAGE_KEY={LOCATION_QUALITY_STORAGE_KEY}
                    hideLocationQuality={isHorodateme}
                    photoQuality={photoQuality}
                    setPhotoQuality={setPhotoQuality}
                    photoOptions={photoOptions}
                    uiPhotoQualityToApi={uiPhotoQualityToApi}
                    autoSave={autoSave}
                    setAutoSave={setAutoSave}
                    toast={toast}
                />
            )}

            {/* Account Sheets */}
            <SheetUserBillingEdit
                open={billingEditOpen}
                onClose={() => {
                    setBillingEditOpen(false);
                    setSheetResetKey((k) => k + 1);
                }}
                user={user}
                resetKey={sheetResetKey}
                isSubmitting={isSubmittingSheet}
                onSubmit={async (payload) => {
                    setIsSubmittingSheet(true);
                    setAccountError('');
                    try {
                        await horodatyApi.updateBilling(payload);
                        const userData = await refreshUser();
                        setUser(userData);
                        setBillingEditOpen(false);
                        setSheetResetKey((k) => k + 1);
                        toast.success(t('page.billingUpdated'));
                    } catch (error) {
                        const message = error?.response?.data?.message || t('page.billingUpdateFailed');
                        setAccountError(message);
                        toast.error(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />

            <SheetUserEmailEdit
                open={emailEditOpen}
                onClose={() => {
                    setEmailEditOpen(false);
                    setSheetResetKey((k) => k + 1);
                }}
                user={user}
                resetKey={sheetResetKey}
                isSubmitting={isSubmittingSheet}
                onSubmit={async (payload) => {
                    setIsSubmittingSheet(true);
                    setAccountError('');
                    try {
                        await horodatyApi.updateEmail(payload);
                        const userData = await refreshUser();
                        setUser(userData);
                        setEmailEditOpen(false);
                        setSheetResetKey((k) => k + 1);
                        toast.success(t('page.emailUpdated'));
                    } catch (error) {
                        const message = error?.response?.data?.message || t('page.emailUpdateFailed');
                        setAccountError(message);
                        toast.error(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />

            <SheetUserPasswordEdit
                open={passwordEditOpen}
                onClose={() => {
                    setPasswordEditOpen(false);
                    setSheetResetKey((k) => k + 1);
                }}
                resetKey={sheetResetKey}
                isSubmitting={isSubmittingSheet}
                onSubmit={async (payload) => {
                    setIsSubmittingSheet(true);
                    setAccountError('');
                    try {
                        await horodatyApi.updatePassword(payload);
                        setPasswordEditOpen(false);
                        setSheetResetKey((k) => k + 1);
                        toast.success(t('page.passwordUpdated'));
                    } catch (error) {
                        const message =
                            error?.response?.data?.message || t('page.passwordUpdateFailed');
                        setAccountError(message);
                        toast.error(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />

            {activeTab === 'company' && (
                <CompanyTabContent
                    company={company}
                    companyLogoUrl={companyLogoUrl}
                    companyBalance={companyBalance}
                    collaborators={collaborators}
                    collaboratorSearch={collaboratorSearch}
                    setCollaboratorSearch={setCollaboratorSearch}
                    activities={activities}
                    isLoadingActivities={isLoadingActivities}
                    companyInfoRef={companyInfoRef}
                    collaboratorsHeight={collaboratorsHeight}
                    setCompanyEditOpen={setCompanyEditOpen}
                    setInviteOpen={setInviteOpen}
                    setEditCollaboratorOpen={setEditCollaboratorOpen}
                    setDeleteCollaboratorOpen={setDeleteCollaboratorOpen}
                    setActivityFilterOpen={setActivityFilterOpen}
                    setSelectedCollaborator={setSelectedCollaborator}
                    setCollaboratorToDelete={setCollaboratorToDelete}
                    setCollaboratorToReactivate={setCollaboratorToReactivate}
                    setReactivateCollaboratorOpen={setReactivateCollaboratorOpen}
                    setSubsidiaryCreateOpen={setSubsidiaryCreateOpen}
                    subsidiaries={subsidiaries}
                    isLoadingSubsidiaries={isLoadingSubsidiaries}
                    refreshSubsidiaries={refreshSubsidiaries}
                    setCompany={setCompany}
                    collaboratorsPagination={{
                        manualPagination: true,
                        pageCount: collaboratorsPageCount,
                        currentPage: collaboratorsPage,
                        onPageChange: setCollaboratorsPage,
                        onPageSizeChange: (size) => {
                            setCollaboratorsPageSize(size);
                            setCollaboratorsPage(1);
                        },
                        pageSize: collaboratorsPageSize,
                        totalCount: collaboratorsTotal,
                        isLoading: isLoadingCollaborators,
                        searchValue: collaboratorSearch,
                        onSearchChange: handleCollaboratorSearchChange,
                    }}
                    subsidiariesPagination={{
                        manualPagination: true,
                        pageCount: subsidiariesPageCount,
                        currentPage: subsidiariesPage,
                        onPageChange: setSubsidiariesPage,
                        onPageSizeChange: (size) => {
                            setSubsidiariesPageSize(size);
                            setSubsidiariesPage(1);
                        },
                        pageSize: subsidiariesPageSize,
                        totalCount: subsidiariesTotal,
                        isLoading: isLoadingSubsidiaries,
                        searchValue: subsidiarySearch,
                        onSearchChange: handleSubsidiarySearchChange,
                    }}
                    activitiesPagination={{
                        pageCount: activitiesPageCount,
                        currentPage: activitiesPage,
                        onPageChange: setActivitiesPage,
                        onPageSizeChange: (size) => {
                            setActivitiesPageSize(size);
                            setActivitiesPage(1);
                        },
                        pageSize: activitiesPageSize,
                        totalCount: activitiesTotal,
                        isLoading: isLoadingActivities,
                    }}
                />
            )}

            {activeTab === 'superadmin' && <SuperAdminTabContent />}

            {/* Invite collaborators sheet */}
            <SheetCompanyInvite
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                resetKey={inviteOpen}
                isSubmitting={isSubmittingSheet}
                hasOwner={collaborators.some((c) => {
                    const role = typeof c.role === 'string' ? c.role : c.role?.name;
                    return role?.toLowerCase() === 'owner';
                })}
                isSuperAdmin={authUser?.role === 'admin'}
                onSubmit={async (payload) => {
                    await processInvites(payload);
                }}
            />

            {/* Edit collaborator sheet */}
            <SheetCollaboratorEdit
                open={editCollaboratorOpen}
                onClose={() => setEditCollaboratorOpen(false)}
                collaborator={selectedCollaborator}
                resetKey={editCollaboratorOpen}
                isSubmitting={isSubmittingSheet}
                onSubmit={async (updated) => {
                    if (!updated?.id) {
                        setEditCollaboratorOpen(false);
                        return;
                    }

                    setIsSubmittingSheet(true);
                    setAccountError('');

                    try {
                        //const name = String(updated?.name || '').trim();
                        //const parts = name.split(/\s+/).filter(Boolean);
                        const first_name = updated?.first_name || '';
                        const last_name = updated?.last_name || '';
                        const email = updated?.email || '';
                        const role = updated?.role || '';

                        await horodatyApi.managerUpdateUser(updated.id, {
                            first_name: first_name || undefined,
                            last_name: last_name || undefined,
                            email: email || undefined,
                            role: role || undefined,
                        });

                        await refreshCollaborators(true);
                        setEditCollaboratorOpen(false);
                        toast.success(t('page.collaboratorUpdated'));
                    } catch (error) {
                        const message =
                            error?.response?.data?.message || t('page.collaboratorUpdateFailed');
                        setAccountError(message);
                        toast.error(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />

            {/* Delete collaborator dialog */}
            <AlertDialogDeleteCollaborator
                open={deleteCollaboratorOpen}
                onOpenChange={setDeleteCollaboratorOpen}
                collaborator={collaboratorToDelete}
                isSubmitting={isSubmittingSheet}
                onConfirm={async () => {
                    if (!collaboratorToDelete?.id) {
                        setDeleteCollaboratorOpen(false);
                        return;
                    }

                    setIsSubmittingSheet(true);
                    setAccountError('');

                    try {
                        await horodatyApi.managerArchiveUser(collaboratorToDelete.id);
                        await refreshCollaborators(true);
                        setDeleteCollaboratorOpen(false);
                        toast.success(t('page.collaboratorArchived'));
                    } catch (error) {
                        const message = error?.response?.data?.message || t('page.collaboratorArchiveFailed');
                        setAccountError(message);
                        toast.error(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />

            {/* Reactivate collaborator dialog */}
            <AlertDialogReactivateCollaborator
                open={reactivateCollaboratorOpen}
                onOpenChange={setReactivateCollaboratorOpen}
                collaborator={collaboratorToReactivate}
                onConfirm={async () => {
                    if (!collaboratorToReactivate?.id) {
                        setReactivateCollaboratorOpen(false);
                        return;
                    }

                    setIsSubmittingSheet(true);
                    try {
                        await horodatyApi.managerRestoreUser(collaboratorToReactivate.id);
                        toast.success(t('page.collaboratorReactivated'));
                        setReactivateCollaboratorOpen(false);
                        // Continue ensuring we process the rest
                        await processInvites(pendingInvites);
                    } catch (error) {
                        const message = error?.response?.data?.message || t('page.collaboratorReactivateFailed');
                        toast.error(message);
                        setIsSubmittingSheet(false);
                    }
                }}
                onCancel={async () => {
                    setReactivateCollaboratorOpen(false);
                    // Skip and continue
                    if (pendingInvites.length > 0) {
                        await processInvites(pendingInvites);
                    } else {
                        // If no more invites, we are done
                        await refreshCollaborators(true);
                        setIsSubmittingSheet(false);
                        toast.success(t('page.invitationsCompleted'));
                    }
                }}
            />

            {/* Edit company sheet */}
            <SheetCompanyEdit
                open={companyEditOpen}
                onClose={() => setCompanyEditOpen(false)}
                company={company}
                companyLogoUrl={companyLogoUrl}
                resetKey={companyEditOpen}
                isSubmitting={isSubmittingSheet}
                onSubmit={async (updated) => {
                    setIsSubmittingSheet(true);
                    setAccountError('');

                    try {
                        // Update company details
                        await horodatyApi.managerUpdateCompany({
                            nom: updated.name,
                            description: updated.description,
                            notes: updated.notes,
                            rue: updated.street,
                            code_postal: updated.postalCode,
                            ville: updated.city,
                            siret: updated.siret,
                            tva: updated.vat,
                            phone: updated.phone,
                            email: updated.email,
                            billing_email: updated.billingEmail || null,
                            ...(updated.billingIdentifier !== undefined && { billing_id: updated.billingIdentifier || null }),
                        });

                        if (companyId && updated?.logoBase64 && updated?.logoFileName) {
                            await horodatyApi.updateCompanyLogo(companyId, {
                                file_name: updated.logoFileName,
                                base64: updated.logoBase64,
                            });
                            await refreshCompanyLogo(true);
                        }

                        await refreshCompany(true);
                        setCompanyEditOpen(false);
                        toast.success(t('page.companyUpdated'));
                    } catch (error) {
                        let message = error?.response?.data?.message || t('page.companyUpdateFailed');

                        if (typeof message === 'object' && message !== null) {
                            const errors = Object.values(message).flat();
                            // Mise à jour du state avec une chaine de caractères
                            message = errors.join('\n');

                            // Affichage d'une liste propre dans le toast
                            toast.error(
                                <div className="space-y-1">
                                    <p className="font-semibold">{t('account:attention')}</p>
                                    <ul className="list-disc pl-4 text-sm">
                                        {errors.map((err, index) => (
                                            <li key={index}>{err}</li>
                                        ))}
                                    </ul>
                                </div>,
                            );
                        } else {
                            toast.error(message);
                        }

                        setAccountError(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />

            {/* Activity filter sheet */}
            <SheetActivityFilter
                open={activityFilterOpen}
                onClose={() => setActivityFilterOpen(false)}
                collaborators={collaborators}
                currentFilter={activityFilter}
                resetKey={activityFilterOpen}
                onSubmit={(filter) => {
                    setActivityFilter(filter);
                    setActivitiesPage(1);
                    setActivityFilterOpen(false);
                }}
            />

            {/* Create subsidiary sheet */}
            <SheetSubsidiaryCreate
                open={subsidiaryCreateOpen}
                onClose={() => setSubsidiaryCreateOpen(false)}
                resetKey={subsidiaryCreateOpen}
                isSubmitting={isSubmittingSheet}
                onSubmit={async (data) => {
                    setIsSubmittingSheet(true);
                    try {
                        await horodatyApi.subsidiaries.create(companyId, {
                            company: {
                                name: data.name,
                                street: data.street || '',
                                cp: data.postalCode || '',
                                city: data.city || '',
                                siret: data.siret,
                                tva: data.vat || null,
                                email: data.email,
                                description: data.description || null,
                                notes: data.notes || null,
                                phone: data.phone || null,
                                authorized_buy_tokens: data.allowTokenPurchase ?? true,
                                ...(data.billingId !== undefined && { billing_id: data.billingId || null }),
                            },
                            owner: data.managerEmail
                                ? {
                                      firstname: data.managerFirstName,
                                      lastname: data.managerLastName,
                                      email: data.managerEmail,
                                  }
                                : undefined,
                        });
                        await refreshSubsidiaries(true);
                        setSubsidiaryCreateOpen(false);
                        toast.success(t('page.subsidiaryAdded'));
                    } catch (error) {
                        const message = error?.response?.data?.message || t('page.subsidiaryAddFailed');
                        toast.error(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />
        </div>
    );
}
